import { type SyntheticEvent, useState } from 'react';

import { requestPasswordReset } from '../api/request-password-reset';
import { requestPasswordResetSchema } from '../model/request-password-reset.schema';

const genericErrorCopy = 'Something went wrong. Please try again.';

type Screen = 'request' | 'sent';

type FieldErrors = {
    email?: string;
    form?: string;
};

export function useRequestPasswordReset() {
    const [screen, setScreen] = useState<Screen>('request');
    const [submittedEmail, setSubmittedEmail] = useState('');
    const [email, setEmail] = useState('');
    const [errors, setErrors] = useState<FieldErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleEmailBlur = () => {
        const result = requestPasswordResetSchema.shape.email.safeParse(email);
        setErrors((prev) => ({
            ...prev,
            email: result.success ? undefined : result.error.issues[0]?.message,
        }));
    };

    const handleSubmit = async (
        event: SyntheticEvent<HTMLFormElement>,
    ): Promise<'email' | undefined> => {
        event.preventDefault();
        if (isSubmitting) return;

        const parsed = requestPasswordResetSchema.safeParse({ email });
        if (!parsed.success) {
            const message = parsed.error.issues[0]?.message;
            setErrors({ email: message });
            return 'email';
        }

        setErrors({});
        setIsSubmitting(true);

        const result = await requestPasswordReset(parsed.data);
        setIsSubmitting(false);

        if (result.ok) {
            setSubmittedEmail(parsed.data.email);
            setScreen('sent');
            return;
        }

        setErrors({ form: genericErrorCopy });
        return undefined;
    };

    return {
        screen,
        submittedEmail,
        email,
        errors,
        isSubmitting,
        setEmail: (value: string) => {
            setEmail(value);
            if (errors.email || errors.form) {
                setErrors((prev) => ({ ...prev, email: undefined, form: undefined }));
            }
        },
        handleEmailBlur,
        handleSubmit,
    };
}
